
import { throttle } from 'lodash'
import { subject } from '@casl/ability'
import API from '../../../vue_shared/api'
import { mapActions, mapState } from 'vuex'
import MessageBox from '@nextrequestco/components/src/components/MessageBox/MessageBox.vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput/nr-form-input.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import NrSelect from '@nextrequestco/components/src/components/NrSelect/nr-select.vue'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation/nr-loading-animation.vue'
import RequestDepartmentAssigned from '../components/RequestDepartmentAssigned.vue'
import RequestPoc from '../components/RequestPoc.vue'

interface Staff {
  id: number
  name_with_email: string // eslint-disable-line camelcase
  name_or_email: string // eslint-disable-line camelcase
}

export default {
  name: 'RequestAssigned',
  components: {
    MessageBox,
    NrIconButton,
    NrFormInput,
    'nr-editor': NrEditor,
    NrModal,
    NrTooltip,
    RequestPoc,
    NrSelect,
    NrLoadingAnimation,
    RequestDepartmentAssigned
  },
  props: {
    account: {
      type: Object
    },
    requestId: {
      type: String
    },
    departments: {
      type: Array
    },
    visibility: {
      type: String
    },
    prettyId: {
      type: String
    },
    poc: {
      type: Object
    },
    requestStaff: {
      type: Array
    }
  },
  data() {
    return {
      addStaffToRequest: [],
      message: '',
      name: '',
      page: 1,
      selectedStaff: [],
      showAddStaffModal: false,
      showRemoveStaffModal: false,
      staffNames: [{ id: '', name: '' }],
      endFetching: false,
      staffHasIncompleteTasks: false,
      sendAllAssignedStaff: false
    }
  },
  computed: {
    ...mapState('request_show', [
      'request',
      'dataFetchState',
      'dataOptions',
      'filteredRequestLoading'
    ]),

    canSeeStaffOnlyItems() {
      return this.$can('read', 'staff_only_items')
    },

    canAddAndRemoveStaff() {
      return (
        this.$ability.can('manage', 'Request') ||
        this.$ability.can('read_helpers', subject('Request', this.request))
      )
    },

    disableRemoveStaffButton() {
      return this.staffHasIncompleteTasks
    },

    removeStaffDisplayText() {
      if (this.staffHasIncompleteTasks) {
        return `${this.staffNameOrEmail(
          this.selectedStaff[0]
        )} has incomplete tasks.
         Please reassign their tasks or mark as complete before attempting to remove
         this staff member from the request.`
      } else {
        return `You are about to remove ${this.staffNameOrEmail(
          this.selectedStaff[0]
        )} from support staff for this request. Do you want to continue?`
      }
    },

    filteredStaff() {
      return this.dataOptions.staff.map((staff: Staff) => ({
        label: staff.name_with_email,
        value: staff.id
      }))
    }
  },
  methods: {
    ...mapActions('request_show', ['submitStaffChange', 'fetchDataOptions']),

    staffNameOrEmail(staff: Staff): string {
      if (!staff) return ''
      return staff.name_or_email
    },

    staffDepartmentsWithPocLabel(staff: Staff): string {
      const departmentsList = []
      this.departments.forEach((department) => {
        // @ts-ignore: NEX-9140
        if (staff.departments.includes(department.name)) {
          if (department.poc_id === staff.id) {
            departmentsList.unshift(department.name + ' (Point of Contact)')
          } else {
            departmentsList.push(department.name)
          }
        }
      })
      return departmentsList.join(', ')
    },

    filterStaff: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'staff' })
    }, 400),

    handleStaffScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.staff.search_text,
        dataType: 'staff'
      })
    }, 400),

    handleAddStaffClick() {
      this.showAddStaffModal = true
    },

    async handleRemoveStaffClick(staff: Staff) {
      this.showRemoveStaffModal = true
      this.selectedStaff = [staff]
      try {
        const response = await API.fetchRequestTasks({
          pretty_id: this.request.pretty_id,
          params: {
            user_id: staff.id,
            check_incomplete_tasks: true
          }
        })
        this.staffHasIncompleteTasks = response.data.incomplete_tasks
      } catch (_error) {
        this.$toasted.error('An error has occured. Please try again.')
      }
    },

    hideRemoveStaffModal() {
      this.showRemoveStaffModal = false
      this.selectedStaff = []
    },

    hideAddStaffModal() {
      this.addStaffToRequest = []
      this.message = ''
      this.name = ''
      this.page = 1
      this.selectedStaff = []
      this.showAddStaffModal = false
      this.staffNames = [{ id: '', name: '' }]
      this.endFetching = false
      this.sendAllAssignedStaff = false
    },

    submitAddStaff() {
      this.submitStaffChange({
        method: 'post',
        action: 'update',
        request_id: this.prettyId,
        staff_ids: this.addStaffToRequest.map(
          (staff: { value: number }) => staff.value
        ),
        message: this.message,
        send_all_assigned_staff: this.sendAllAssignedStaff
      }).then(() => {
        this.hideAddStaffModal()
      })
    },

    submitRemoveStaff() {
      this.submitStaffChange({
        method: 'delete',
        action: 'remove',
        request_id: this.prettyId,
        id: this.selectedStaff[0].id
      }).then(() => {
        this.hideRemoveStaffModal()
      })
    }
  }
}
